/**
 * VISTOERA_BILLING_PLAN_CREDITS_TOPUP_PAYG_REWARDS_AND_PROMOTIONS_
 * FOUNDATION_P0_V1 — "积分流水" tab (task spec §13, append-only Credit
 * Ledger browse). Platform-Admin read-only view across every
 * workspace's ledger entries, optionally filtered to one
 * `workspace_id` — never a mutation surface itself (every entry here
 * was already written by some other write path: Plan grant / TopUp /
 * Promotion / Daily Reward / AI Usage / Manual Adjustment / Refund).
 */
import { Alert, Input, Table, Tag, Typography } from 'antd'
import { useEffect, useState } from 'react'
import { platformListCreditLedgerAdmin } from '../../api'
import type { BillingCreditLedgerEntry } from '../../types'
import { SectionCard } from '../adminShared'
import { errMsg, formatCredits, formatDateTimeShort } from './billingShared'

const TRANSACTION_TYPE_LABEL: Record<string, string> = {
  plan_grant: '套餐赠送',
  topup_purchase: '充值到账',
  promotion_bonus: '活动赠送',
  daily_reward_claim: '每日领取',
  registration_reward: '注册赠送',
  usage_charge: 'AI 使用扣费',
  manual_adjustment: '人工调整',
  refund: '退款',
  reversal: '冲正',
  expiration: '过期',
}

export default function CreditLedgerTab() {
  const [entries, setEntries] = useState<BillingCreditLedgerEntry[]>([])
  const [total, setTotal] = useState(0)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [workspaceId, setWorkspaceId] = useState('')
  const [page, setPage] = useState(1)
  const pageSize = 50

  const load = () => {
    setLoading(true)
    setError(null)
    platformListCreditLedgerAdmin({ workspace_id: workspaceId || undefined, limit: pageSize, offset: (page - 1) * pageSize })
      .then((res) => {
        setEntries(res.data.items)
        setTotal(res.data.total)
      })
      .catch((err) => setError(errMsg(err, '加载积分流水失败')))
      .finally(() => setLoading(false))
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(load, [page, workspaceId])

  return (
    <div>
      {error && <Alert type="error" message={error} showIcon style={{ marginBottom: 12 }} />}
      <SectionCard title="积分流水（Credit Ledger）">
        <Typography.Paragraph type="secondary" style={{ margin: '0 4px 10px', fontSize: 12 }}>
          不可变（append-only）流水：每一笔积分增减都可追溯到 workspace / 变动原因 / 变动前后余额 / 来源单据。
        </Typography.Paragraph>
        <Input.Search
          allowClear
          placeholder="按 Workspace ID 筛选"
          style={{ width: 280, marginBottom: 10 }}
          value={workspaceId}
          onChange={(e) => setWorkspaceId(e.target.value)}
          onSearch={() => {
            setPage(1)
            load()
          }}
        />
        <Table
          className="pf-data-table"
          size="small"
          rowKey="id"
          loading={loading}
          dataSource={entries}
          pagination={{ current: page, pageSize, total, onChange: setPage, showSizeChanger: false }}
          columns={[
            { title: '时间', dataIndex: 'created_at', width: 160, render: (v: string) => formatDateTimeShort(v) },
            { title: 'Workspace', dataIndex: 'workspace_id', width: 160, ellipsis: true },
            {
              title: '类型',
              dataIndex: 'transaction_type',
              width: 130,
              render: (v: string) => <Tag>{TRANSACTION_TYPE_LABEL[v] ?? v}</Tag>,
            },
            {
              title: '方向',
              dataIndex: 'direction',
              width: 70,
              render: (v: string) => <Tag color={v === 'credit' ? 'green' : 'red'}>{v === 'credit' ? '+' : '-'}</Tag>,
            },
            { title: '变动数量', dataIndex: 'amount', width: 110, align: 'right' as const, render: (v: string) => formatCredits(v) },
            { title: '变动前', dataIndex: 'balance_before', width: 110, align: 'right' as const, render: (v: string) => formatCredits(v) },
            { title: '变动后', dataIndex: 'balance_after', width: 110, align: 'right' as const, render: (v: string) => formatCredits(v) },
            { title: '来源', dataIndex: 'source_type', width: 130, render: (v: string | null) => v ?? '—' },
            { title: '说明', dataIndex: 'description', ellipsis: true, render: (v: string | null) => v ?? '—' },
            { title: '操作人', dataIndex: 'actor', width: 140, render: (v: string | null) => v ?? '—' },
          ]}
        />
      </SectionCard>
    </div>
  )
}
